import { useContext, ReactNode } from "react";
import { Grid } from "@mui/material";
import { useParams } from "react-router-dom";
import { UserContext } from "src/Utils/Context/UserContext";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { isAnyFeatureEnabled } from "src/helpers/FeatureCapabilityConfig";
import { CircularLoader } from "src/design/CircularLoader";
import { NoAccess } from "src/design/NoAccess";

interface CapabilityBoundaryProps {
  /**
   * The feature the route sells. A list gates on the union — the booking list
   * is reachable with full booking management or with the same-day slice alone,
   * and the page itself decides how much of the module to render.
   */
  feature: string | string[];
  children: ReactNode;
}

export const CapabilityBoundary = ({
  feature,
  children,
}: CapabilityBoundaryProps) => {
  const { user, isLoading: userLoading } = useContext(UserContext);
  const { restaurants, isLoading: restaurantsLoading } =
    useContext(RestaurantContext);
  const { restaurantId } = useParams<{ restaurantId: string }>();

  const isLoading = userLoading || restaurantsLoading;

  if (isLoading) {
    return (
      <Grid container justifyContent="center" sx={{ mt: 3 }}>
        <Grid item>
          <CircularLoader />
        </Grid>
      </Grid>
    );
  }

  const restaurant = restaurants.find((r) => r.id === restaurantId) ?? null;

  // Render a neutral fallback rather than redirecting: the previous redirect
  // target was the home route, which is itself capability-guarded now and would
  // loop. Showing NoAccess in place avoids any redirect cycle.
  if (
    !restaurant ||
    !user ||
    !isAnyFeatureEnabled(
      Array.isArray(feature) ? feature : [feature],
      restaurant.capabilities.map((cap) => cap.name),
      user,
    )
  ) {
    return <NoAccess />;
  }

  return <>{children}</>;
};
